import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  LinearProgress,
  Link,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import TravelExploreIcon from "@mui/icons-material/TravelExplore";
import { toast } from "react-toastify";
import api from "../../api";
import TakeActionDialog from "./TakeActionDialog";

const PRIORITY_COLOR = { high: "error", medium: "warning", low: "info" };
const TABLE_HEAD_SX = {
  "& .MuiTableCell-head": { fontWeight: 700, whiteSpace: "nowrap", bgcolor: "#f8fafc", color: "#374151" },
};
const KPI_ITEMS = [
  { label: "New products", key: "pending_releases", tab: 0 },
  { label: "Active campaigns", key: "active_campaigns", tab: 1 },
  { label: "School diffs", key: "pending_diffs", tab: 2 },
  { label: "High-priority actions", key: "high_priority_actions", tab: null },
  { label: "Competitors tracked", key: "competitors_tracked", tab: 3 },
];
const CHECK_LABEL = {
  verified: "Certain",
  needs_review: "Needs a person",
  disagreed: "Page disagrees",
  unverifiable: "Page not read",
};

function priorityChip(priority) {
  const label = (priority || "low").replace(/^./, (c) => c.toUpperCase());
  return <Chip size="small" label={label} color={PRIORITY_COLOR[priority] || "info"} />;
}

function plainLabel(value) {
  if (!value) return "—";
  const text = String(value).replace(/_/g, " ");
  return text.replace(/^./, (c) => c.toUpperCase());
}

function checkCounts(status) {
  if (!status || status.running) return null;
  if (typeof status.checked === "number") {
    return {
      checked: status.checked,
      wrote: status.applied ?? 0,
      review: status.needs_review ?? 0,
      disagreed: status.disagreed ?? 0,
      unread: status.unverifiable ?? 0,
    };
  }
  const match = String(status.summary || "").match(/Checked (\d+)\. Wrote (\d+).*?(\d+) need a person, (\d+) disagree.*?(\d+) had no page/);
  if (!match) return null;
  return {
    checked: Number(match[1]),
    wrote: Number(match[2]),
    review: Number(match[3]),
    disagreed: Number(match[4]),
    unread: Number(match[5]),
  };
}

function formatWhen(value) {
  if (!value) return "Never";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function endsLabel(endsAt) {
  if (!endsAt) return null;
  const end = new Date(endsAt);
  if (Number.isNaN(end.getTime())) return null;
  const days = Math.ceil((end.setHours(23, 59, 59, 999) - Date.now()) / 86400000);
  if (days < 0) return "ended";
  if (days === 0) return "ends today";
  return `ends in ${days} day${days === 1 ? "" : "s"}`;
}

function errorMessage(err, fallback) {
  return err?.response?.data?.message || fallback;
}

function DiffPreviewList({ rows }) {
  return (
    <Stack spacing={1.5} sx={{ mt: 1.5 }}>
      {rows.map((row) => (
        <Box key={row.id} sx={{ border: "1px solid #e5e7eb", borderRadius: 1, p: 1.5 }}>
          <Typography variant="body2" fontWeight={700}>{row.school_name} — {row.field_changed}</Typography>
          <Grid container spacing={1} sx={{ mt: 0.25 }}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ bgcolor: "#fdecea", p: 1, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Current</Typography>
                <Typography variant="body2" sx={{ textDecoration: "line-through" }}>{row.internal_data || "Empty"}</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box sx={{ bgcolor: "#e8f5e9", p: 1, borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary">Might become</Typography>
                <Typography variant="body2">{row.official_data || "Empty"}</Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      ))}
    </Stack>
  );
}

function ListPager({ count, page, rows, onPage, onRows }) {
  return (
    <TablePagination
      component="div"
      count={count}
      page={count === 0 ? 0 : page}
      onPageChange={(_, next) => onPage(next)}
      rowsPerPage={rows}
      onRowsPerPageChange={(e) => onRows(parseInt(e.target.value, 10))}
      rowsPerPageOptions={[10, 20, 50]}
    />
  );
}

export default function CompetitiveDashboard() {
  const [data, setData] = useState(null);
  const [competitors, setCompetitors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState(0);
  const [competitorFilter, setCompetitorFilter] = useState("");
  const [competitorSearch, setCompetitorSearch] = useState("");
  const [actionTarget, setActionTarget] = useState(null);
  // One filter per column (29 Oct meeting: "every column wise we can type").
  const [competitorColumns, setCompetitorColumns] = useState({ name: "", domain: "", via: "", scanned: "", tracking: "" });
  const [competitorPage, setCompetitorPage] = useState(0);
  const [competitorRows, setCompetitorRows] = useState(10);
  const [releaseFilter, setReleaseFilter] = useState({ priority: "", type: "", search: "" });
  const [releasePage, setReleasePage] = useState(0);
  const [releaseRows, setReleaseRows] = useState(20);
  const [campaignStatus, setCampaignStatus] = useState("active");
  const [campaignFilter, setCampaignFilter] = useState({ priority: "", type: "", search: "" });
  const [campaignPage, setCampaignPage] = useState(0);
  const [campaignRows, setCampaignRows] = useState(20);
  const [actionFilter, setActionFilter] = useState({ priority: "", search: "" });
  const [actionPage, setActionPage] = useState(0);
  const [actionRows, setActionRows] = useState(10);
  const [busy, setBusy] = useState({});
  const [addOpen, setAddOpen] = useState(false);
  const [confirmUpdate, setConfirmUpdate] = useState(null);
  const [previewRows, setPreviewRows] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewSeq = useRef(0);
  const [draft, setDraft] = useState({ name: "", domain: "", notes: "" });
  const [scanStatus, setScanStatus] = useState(null);
  const [verifyStatus, setVerifyStatus] = useState(null);
  // School diffs: High + Medium by default (low is mostly contact details).
  // Source "school" hides tutor guides and the Eleven Plus Exams directory.
  const [diffFilter, setDiffFilter] = useState({
    priority: "high,medium",
    group: "",
    search: "",
    kind: "",
    verification: "",
    source: "school",
    region: "",
  });
  const [diffPage, setDiffPage] = useState(0);
  const [diffRows, setDiffRows] = useState(20);
  const [listsLoading, setListsLoading] = useState(false);

  const listParams = useCallback(() => ({
    diffs_priority: diffFilter.priority,
    diffs_group: diffFilter.group || undefined,
    diffs_search: diffFilter.search.trim() || undefined,
    diffs_kind: diffFilter.kind || undefined,
    diffs_verification: diffFilter.verification || undefined,
    diffs_source: diffFilter.source || undefined,
    diffs_region: diffFilter.region || undefined,
    diffs_page: diffPage + 1,
    diffs_per_page: diffRows,
    releases_page: releasePage + 1,
    releases_per_page: releaseRows,
    releases_priority: releaseFilter.priority || undefined,
    releases_type: releaseFilter.type || undefined,
    releases_search: releaseFilter.search.trim() || undefined,
    releases_competitor: competitorFilter || undefined,
    campaigns_page: campaignPage + 1,
    campaigns_per_page: campaignRows,
    campaigns_status: campaignStatus,
    campaigns_priority: campaignFilter.priority || undefined,
    campaigns_type: campaignFilter.type || undefined,
    campaigns_search: campaignFilter.search.trim() || undefined,
    campaigns_competitor: competitorFilter || undefined,
    actions_page: actionPage + 1,
    actions_per_page: actionRows,
    actions_priority: actionFilter.priority || undefined,
    actions_search: actionFilter.search.trim() || undefined,
  }), [
    diffFilter, diffPage, diffRows,
    releaseFilter, releasePage, releaseRows,
    campaignStatus, campaignFilter, campaignPage, campaignRows,
    actionFilter, actionPage, actionRows,
    competitorFilter,
  ]);
  const listParamsRef = useRef(listParams);
  listParamsRef.current = listParams;
  const requestSeq = useRef(0);
  const searchRef = useRef("");
  const skipListFetch = useRef(true);

  const applyDashboard = useCallback((next, prev) => {
    if (!prev) return next;
    return {
      ...prev,
      kpis: next.kpis,
      releases: next.releases,
      releases_meta: next.releases_meta,
      release_types: next.release_types,
      campaigns: next.campaigns,
      campaigns_meta: next.campaigns_meta,
      campaign_types: next.campaign_types,
      diffs: next.diffs,
      diffs_meta: next.diffs_meta,
      diffs_regions: next.diffs_regions,
      actions: next.actions,
      actions_meta: next.actions_meta,
    };
  }, []);

  const load = useCallback(async (signal) => {
    const id = ++requestSeq.current;
    const [dashboard, list] = await Promise.all([
      api.get("/admin/competitive-intel", { signal, params: listParamsRef.current() }),
      api.get("/admin/competitive-intel/competitors", { signal }),
    ]);
    if (signal?.aborted) return;
    setCompetitors(list.data.data || []);
    if (id !== requestSeq.current) return;
    setData(dashboard.data.data);
  }, []);

  const loadLists = useCallback(async () => {
    const id = ++requestSeq.current;
    setListsLoading(true);
    try {
      const res = await api.get("/admin/competitive-intel", { params: listParamsRef.current() });
      if (id !== requestSeq.current) return;
      const next = res.data.data;
      setData((prev) => applyDashboard(next, prev));
    } catch (err) {
      if (id === requestSeq.current) toast.error(errorMessage(err, "Could not load the list."));
    } finally {
      if (id === requestSeq.current) setListsLoading(false);
    }
  }, [applyDashboard]);

  useEffect(() => {
    const params = listParams();
    const signature = [params.releases_search, params.campaigns_search, params.diffs_search, params.actions_search].join("\n");
    const wait = signature !== searchRef.current ? 400 : 0;
    searchRef.current = signature;
    if (skipListFetch.current) {
      skipListFetch.current = false;
      return undefined;
    }
    const timer = setTimeout(() => { loadLists(); }, wait);
    return () => clearTimeout(timer);
  }, [listParams, loadLists]);

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    load(controller.signal)
      .catch((err) => {
        if (err?.code !== "ERR_CANCELED") {
          toast.error(errorMessage(err, "Could not load competitive intelligence."));
        }
      })
      .finally(() => setLoading(false));
    return () => controller.abort();
  }, [load]);

  useEffect(() => {
    let timer;
    let wasRunning = false;
    let verifyWasRunning = false;
    const poll = () => {
      Promise.all([
        api.get("/admin/competitive-intel/scan-status"),
        api.get("/admin/competitive-intel/diffs/verify-status"),
      ])
        .then(([scanRes, verifyRes]) => {
          const status = scanRes.data.data;
          const verify = verifyRes.data.data;
          setScanStatus(status);
          setVerifyStatus(verify);
          if ((wasRunning && status && !status.running) || (verifyWasRunning && verify && !verify.running)) {
            load().catch(() => {});
          }
          wasRunning = !!status?.running;
          verifyWasRunning = !!verify?.running;
        })
        .catch(() => {});
    };
    poll();
    timer = setInterval(poll, 3000);
    return () => clearInterval(timer);
  }, [load]);

  const markBusy = (key, on) => setBusy((prev) => ({ ...prev, [key]: on }));

  const releases = data?.releases || [];
  const campaigns = data?.campaigns || [];
  const competitorSources = useMemo(
    () => Array.from(new Set(competitors.map((row) => row.discovered_via).filter(Boolean))).sort(),
    [competitors]
  );
  const visibleCompetitors = useMemo(() => {
    const query = competitorSearch.trim().toLowerCase();
    const has = (value, needle) => !needle.trim() || String(value || "").toLowerCase().includes(needle.trim().toLowerCase());
    const weekAgo = Date.now() - 7 * 24 * 60 * 60 * 1000;
    const f = competitorColumns;
    return competitors.filter((row) => {
      if (query && !`${row.name} ${row.domain || ""}`.toLowerCase().includes(query)) return false;
      if (!has(row.name, f.name) || !has(row.domain, f.domain)) return false;
      if (f.via && row.discovered_via !== f.via) return false;
      const scannedAt = row.last_scanned_at ? new Date(row.last_scanned_at).getTime() : null;
      if (f.scanned === "never" && scannedAt) return false;
      if (f.scanned === "week" && !(scannedAt && scannedAt >= weekAgo)) return false;
      if (f.scanned === "older" && !(scannedAt && scannedAt < weekAgo)) return false;
      if (f.tracking === "on" && !row.is_tracked) return false;
      if (f.tracking === "off" && row.is_tracked) return false;
      return true;
    });
  }, [competitors, competitorSearch, competitorColumns]);
  const setCompetitorColumn = (key, value) => {
    setCompetitorPage(0);
    setCompetitorColumns((prev) => ({ ...prev, [key]: value }));
  };

  async function acknowledgeRelease(id) {
    const key = `release-${id}`;
    markBusy(key, true);
    try {
      await api.post(`/admin/competitive-intel/releases/${id}/acknowledge`);
      let stepBack = false;
      setData((prev) => {
        const next = prev.releases.filter((row) => row.id !== id);
        stepBack = next.length === 0 && releasePage > 0;
        return {
          ...prev,
          releases: next,
          releases_meta: prev.releases_meta
            ? { ...prev.releases_meta, total: Math.max(0, prev.releases_meta.total - 1) }
            : prev.releases_meta,
          kpis: { ...prev.kpis, pending_releases: Math.max(0, prev.kpis.pending_releases - 1) },
        };
      });
      if (stepBack) setReleasePage((page) => Math.max(0, page - 1));
      toast.success("Release acknowledged.");
    } catch (err) {
      toast.error(errorMessage(err, "Could not acknowledge the release."));
    } finally {
      markBusy(key, false);
    }
  }

  async function acknowledgeCampaign(id) {
    const key = `campaign-${id}`;
    markBusy(key, true);
    try {
      await api.post(`/admin/competitive-intel/campaigns/${id}/acknowledge`);
      setData((prev) => ({
        ...prev,
        campaigns: prev.campaigns.map((row) =>
          row.id === id ? { ...row, acknowledged_at: new Date().toISOString() } : row
        ),
      }));
      toast.success("Campaign acknowledged.");
    } catch (err) {
      toast.error(errorMessage(err, "Could not acknowledge the campaign."));
    } finally {
      markBusy(key, false);
    }
  }

  async function resolveDiff(id, status) {
    const key = `diff-${id}`;
    markBusy(key, true);
    try {
      await api.post(`/admin/competitive-intel/diffs/${id}/resolve`, { status });
      let stepBack = false;
      setData((prev) => {
        const next = prev.diffs.filter((row) => row.id !== id);
        stepBack = next.length === 0 && diffPage > 0;
        return {
          ...prev,
          diffs: next,
          diffs_meta: prev.diffs_meta ? { ...prev.diffs_meta, total: Math.max(0, prev.diffs_meta.total - 1) } : prev.diffs_meta,
          kpis: { ...prev.kpis, pending_diffs: Math.max(0, prev.kpis.pending_diffs - 1) },
        };
      });
      if (stepBack) setDiffPage((page) => Math.max(0, page - 1));
      toast.success(status === "approved" ? "School updated." : "Difference rejected.");
    } catch (err) {
      toast.error(errorMessage(err, "Could not resolve the difference."));
    } finally {
      markBusy(key, false);
    }
  }

  async function completeAction(action) {
    const key = `action-${action.id}`;
    markBusy(key, true);
    try {
      const res = await api.post(`/admin/competitive-intel/actions/${action.id}/complete`);
      const completed = !!res.data.data.is_completed;
      setData((prev) => {
        const wasHighOpen = action.priority === "high" && !action.is_completed;
        const nowHighOpen = action.priority === "high" && !completed;
        let high = prev.kpis.high_priority_actions;
        if (wasHighOpen && !nowHighOpen) high = Math.max(0, high - 1);
        if (!wasHighOpen && nowHighOpen) high += 1;
        let open = prev.actions_meta?.open ?? 0;
        if (!action.is_completed && completed) open = Math.max(0, open - 1);
        if (action.is_completed && !completed) open += 1;
        return {
          ...prev,
          actions: prev.actions.map((row) => (row.id === action.id ? { ...row, is_completed: completed } : row)),
          actions_meta: prev.actions_meta ? { ...prev.actions_meta, open } : prev.actions_meta,
          kpis: { ...prev.kpis, high_priority_actions: high },
        };
      });
    } catch (err) {
      toast.error(errorMessage(err, "Could not update the task."));
    } finally {
      markBusy(key, false);
    }
  }

  async function scanNow(competitorId) {
    const key = competitorId ? `scan-${competitorId}` : "scan";
    markBusy(key, true);
    try {
      const res = await api.post("/admin/competitive-intel/scan", competitorId ? { competitor_id: competitorId } : {});
      toast.success(res.data.message || "Scan started.");
      setScanStatus((prev) => ({ ...(prev || {}), running: true, phase: "competitors" }));
    } catch (err) {
      toast.error(errorMessage(err, "Could not start the scan."));
    } finally {
      markBusy(key, false);
    }
  }

  async function openConfirm(mode) {
    const id = ++previewSeq.current;
    setConfirmUpdate(mode);
    setPreviewRows([]);
    setPreviewLoading(true);
    const current = listParams();
    const params = mode === "dates"
      ? { diffs_priority: "high", diffs_group: "dates", diffs_source: "school", limit: 25 }
      : {
          diffs_priority: current.diffs_priority,
          diffs_group: current.diffs_group,
          diffs_search: current.diffs_search,
          diffs_kind: current.diffs_kind,
          diffs_verification: current.diffs_verification,
          diffs_source: current.diffs_source,
          diffs_region: current.diffs_region,
          limit: 25,
        };
    try {
      const res = await api.get("/admin/competitive-intel/diffs/preview", { params });
      if (id !== previewSeq.current) return;
      setPreviewRows(res.data.data || []);
    } catch (err) {
      if (id !== previewSeq.current) return;
      toast.error(errorMessage(err, "Could not load the rows that might change."));
      setConfirmUpdate(null);
    } finally {
      if (id === previewSeq.current) setPreviewLoading(false);
    }
  }

  function closeConfirm() {
    previewSeq.current += 1;
    setConfirmUpdate(null);
    setPreviewLoading(false);
  }

  async function verifySchools(overrides) {
    markBusy("verify", true);
    try {
      const params = listParams();
      const res = await api.post("/admin/competitive-intel/diffs/verify", overrides || {
        diffs_priority: params.diffs_priority,
        diffs_group: params.diffs_group,
        diffs_search: params.diffs_search,
        diffs_kind: params.diffs_kind,
        diffs_verification: params.diffs_verification,
        diffs_source: params.diffs_source,
        diffs_region: params.diffs_region,
        apply: true,
        limit: 25,
      });
      toast.success(res.data.message || "Checking school pages.");
      setVerifyStatus((prev) => ({ ...(prev || {}), running: true, phase: "schools" }));
    } catch (err) {
      toast.error(errorMessage(err, "Could not start the school-page check."));
    } finally {
      markBusy("verify", false);
    }
  }

  async function toggleTracked(competitor) {
    const key = `track-${competitor.id}`;
    markBusy(key, true);
    try {
      const res = await api.patch(`/admin/competitive-intel/competitors/${competitor.id}`, {
        is_tracked: !competitor.is_tracked,
      });
      setCompetitors((prev) => prev.map((row) => (row.id === competitor.id ? res.data.data : row)));
      setData((prev) => ({
        ...prev,
        kpis: {
          ...prev.kpis,
          competitors_tracked: prev.kpis.competitors_tracked + (competitor.is_tracked ? -1 : 1),
        },
      }));
    } catch (err) {
      toast.error(errorMessage(err, "Could not update tracking."));
    } finally {
      markBusy(key, false);
    }
  }

  async function addCompetitor() {
    markBusy("add", true);
    try {
      const res = await api.post("/admin/competitive-intel/competitors", draft);
      setCompetitors((prev) => [...prev, res.data.data].sort((a, b) => a.name.localeCompare(b.name)));
      setData((prev) => ({
        ...prev,
        kpis: { ...prev.kpis, competitors_tracked: prev.kpis.competitors_tracked + 1 },
      }));
      setDraft({ name: "", domain: "", notes: "" });
      setAddOpen(false);
      toast.success("Competitor added. It will be scanned on the next run.");
    } catch (err) {
      toast.error(errorMessage(err, "Could not add the competitor."));
    } finally {
      markBusy("add", false);
    }
  }

  const kpis = data?.kpis;
  const check = checkCounts(verifyStatus);
  const openTasks = data?.actions_meta?.open ?? (data?.actions || []).filter((action) => !action.is_completed).length;

  function openKpi(item) {
    if (item.tab != null) setTab(item.tab);
    if (item.tab == null) {
      document.getElementById("intel-tasks")?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    }
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={1}>
        <Box>
          <Typography variant="caption" sx={{ color: "#374151", fontWeight: 700 }}>
            Dashboard / <Box component="span" sx={{ color: "#3B2A9F", fontWeight: 600 }}>Competitive Intelligence</Box>
          </Typography>
          <Typography variant="h5" fontWeight={700} mt={0.5}>
            Competitive Intelligence
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Last scan: {formatWhen(kpis?.last_scan_at)}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <TextField
            select
            size="small"
            label="Competitor"
            value={competitorFilter}
            onChange={(e) => {
              setCompetitorFilter(e.target.value);
              setReleasePage(0);
              setCampaignPage(0);
            }}
            sx={{ minWidth: 180, bgcolor: "white" }}
          >
            <MenuItem value="">All competitors</MenuItem>
            {competitors.map((row) => (
              <MenuItem key={row.id} value={String(row.id)}>{row.name}</MenuItem>
            ))}
          </TextField>
          <Button
            variant="contained"
            startIcon={<TravelExploreIcon />}
            disabled={!!busy.scan}
            onClick={() => scanNow()}
          >
            Scan now
          </Button>
          <Button
            variant="outlined"
            disabled={!!busy.verify || !!verifyStatus?.running}
            onClick={() => openConfirm("dates")}
          >
            Update school dates
          </Button>
        </Stack>
      </Stack>

      {scanStatus?.running && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Agent scanning {scanStatus.current_target || scanStatus.phase} ({scanStatus.processed || 0}/{scanStatus.total || 0})
          </Typography>
          <LinearProgress
            variant={scanStatus.total ? "determinate" : "indeterminate"}
            value={scanStatus.total ? Math.min(100, ((scanStatus.processed || 0) / scanStatus.total) * 100) : 0}
          />
        </Box>
      )}

      {verifyStatus?.running && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            Checking {verifyStatus.current_target || "school pages"} ({verifyStatus.processed || 0}/{verifyStatus.total || 0}). A value is written only when the page states it.
          </Typography>
          <LinearProgress
            variant={verifyStatus.total ? "determinate" : "indeterminate"}
            value={verifyStatus.total ? Math.min(100, ((verifyStatus.processed || 0) / verifyStatus.total) * 100) : 0}
          />
        </Box>
      )}
      {check && (
        <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" sx={{ mt: 2, p: 1.25, bgcolor: "white", border: "1px solid #e5e7eb", borderRadius: 1 }}>
          <Typography variant="body2" fontWeight={700} sx={{ mr: 0.5 }}>School-page check</Typography>
          <Chip size="small" label={`Checked ${check.checked}`} />
          <Chip size="small" color="success" variant="outlined" label={`Wrote ${check.wrote}`} />
          <Chip size="small" color="warning" variant="outlined" label={`${check.review} need a person`} />
          <Chip size="small" color="error" variant="outlined" label={`${check.disagreed} disagree`} />
          <Chip size="small" variant="outlined" label={`${check.unread} page not read`} />
        </Stack>
      )}

      <Stack direction="row" flexWrap="wrap" spacing={1.5} useFlexGap sx={{ mt: 2 }}>
        {KPI_ITEMS.map((item) => {
          const selected = item.tab != null && item.tab === tab;
          return (
            <Card
              key={item.key}
              component="button"
              type="button"
              onClick={() => openKpi(item)}
              sx={{
                flex: "1 1 148px",
                textAlign: "left",
                cursor: "pointer",
                font: "inherit",
                color: "inherit",
                backgroundColor: "white",
                border: selected ? "1px solid #3B2A9F" : "1px solid #e5e7eb",
                borderTop: `3px solid ${selected ? "#3B2A9F" : "#e5e7eb"}`,
                "&:hover": { bgcolor: "#fafafa" },
              }}
            >
              <CardContent sx={{ py: 1.5, "&:last-child": { pb: 1.5 } }}>
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>{item.label}</Typography>
                <Typography variant="h4" fontWeight={700}>{loading ? "—" : (kpis?.[item.key] ?? 0)}</Typography>
              </CardContent>
            </Card>
          );
        })}
      </Stack>

      <Grid container spacing={2} sx={{ mt: 0.5 }}>
        <Grid item xs={12} lg={8}>
          <Card>
            <Tabs value={tab} onChange={(_, value) => setTab(value)} variant="scrollable">
              <Tab label="New releases" />
              <Tab label="Live campaigns" />
              <Tab label="School diffs" />
              <Tab label="Competitors" />
            </Tabs>
            <CardContent>
              {tab === 0 && (
                <>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} flexWrap="wrap" useFlexGap sx={{ mb: 1.5, p: 1.5, bgcolor: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 1 }}>
                  <TextField select size="small" label="Priority" value={releaseFilter.priority} sx={{ minWidth: 140 }}
                    onChange={(e) => { setReleasePage(0); setReleaseFilter((f) => ({ ...f, priority: e.target.value })); }}>
                    <MenuItem value="">All</MenuItem>
                    <MenuItem value="high">High</MenuItem>
                    <MenuItem value="medium">Medium</MenuItem>
                    <MenuItem value="low">Low</MenuItem>
                  </TextField>
                  <TextField select size="small" label="Type" value={releaseFilter.type} sx={{ minWidth: 160 }}
                    onChange={(e) => { setReleasePage(0); setReleaseFilter((f) => ({ ...f, type: e.target.value })); }}>
                    <MenuItem value="">All types</MenuItem>
                    {(data?.release_types || []).map((type) => (
                      <MenuItem key={type} value={type}>{plainLabel(type)}</MenuItem>
                    ))}
                  </TextField>
                  <TextField size="small" label="Search" placeholder="Product or competitor" value={releaseFilter.search} sx={{ minWidth: 220 }}
                    onChange={(e) => { setReleasePage(0); setReleaseFilter((f) => ({ ...f, search: e.target.value })); }} />
                  <Button size="small" onClick={() => { setReleasePage(0); setReleaseFilter({ priority: "", type: "", search: "" }); }}>
                    Reset
                  </Button>
                </Stack>
                {listsLoading && <LinearProgress sx={{ mb: 1 }} />}
                <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
                <Table size="small" sx={{ minWidth: 720 }}>
                  <TableHead sx={TABLE_HEAD_SX}>
                    <TableRow>
                      <TableCell>Competitor</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Priority</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {releases.length === 0 && (
                      <TableRow><TableCell colSpan={5}>No unacknowledged releases.</TableCell></TableRow>
                    )}
                    {releases.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                          <Typography variant="body2" fontWeight={700}>{row.competitor_name}</Typography>
                          {row.competitor?.domain && (
                            <Typography variant="caption" display="block" color="text.secondary">{row.competitor.domain}</Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>{plainLabel(row.product_type)}</TableCell>
                        <TableCell sx={{ verticalAlign: "top", minWidth: 280 }}>
                          <Typography variant="body2" sx={{ lineHeight: 1.45 }}>{row.description}</Typography>
                          {row.source_url && (
                            <Link href={row.source_url} target="_blank" rel="noreferrer" sx={{ display: "inline-block", mt: 0.5 }}>Source</Link>
                          )}
                        </TableCell>
                        <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>{priorityChip(row.priority)}</TableCell>
                        <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap", width: 140 }}>
                          <Stack spacing={0.75} alignItems="flex-start">
                            <Button size="small" variant="contained" disabled={!!busy[`release-${row.id}`]}
                              onClick={() => setActionTarget({ kind: "releases", id: row.id, competitor: row.competitor_name, summary: row.description })}>
                              Take action
                            </Button>
                            <Button size="small" variant="outlined" disabled={!!busy[`release-${row.id}`]} onClick={() => acknowledgeRelease(row.id)}>
                              Acknowledge
                            </Button>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </TableContainer>
                <ListPager
                  count={data?.releases_meta?.total ?? releases.length}
                  page={releasePage}
                  rows={releaseRows}
                  onPage={setReleasePage}
                  onRows={(rows) => { setReleaseRows(rows); setReleasePage(0); }}
                />
                </>
              )}

              {tab === 1 && (
                <>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} flexWrap="wrap" useFlexGap sx={{ mb: 1.5, p: 1.5, bgcolor: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 1 }}>
                    <TextField
                      select
                      size="small"
                      label="Status"
                      value={campaignStatus}
                      onChange={(e) => { setCampaignPage(0); setCampaignStatus(e.target.value); }}
                      sx={{ minWidth: 140 }}
                    >
                      <MenuItem value="active">Active</MenuItem>
                      <MenuItem value="expired">Expired</MenuItem>
                      <MenuItem value="">All</MenuItem>
                    </TextField>
                    <TextField select size="small" label="Priority" value={campaignFilter.priority} sx={{ minWidth: 140 }}
                      onChange={(e) => { setCampaignPage(0); setCampaignFilter((f) => ({ ...f, priority: e.target.value })); }}>
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="high">High</MenuItem>
                      <MenuItem value="medium">Medium</MenuItem>
                      <MenuItem value="low">Low</MenuItem>
                    </TextField>
                    <TextField select size="small" label="Type" value={campaignFilter.type} sx={{ minWidth: 160 }}
                      onChange={(e) => { setCampaignPage(0); setCampaignFilter((f) => ({ ...f, type: e.target.value })); }}>
                      <MenuItem value="">All types</MenuItem>
                      {(data?.campaign_types || []).map((type) => (
                        <MenuItem key={type} value={type}>{plainLabel(type)}</MenuItem>
                      ))}
                    </TextField>
                    <TextField size="small" label="Search" placeholder="Title or offer" value={campaignFilter.search} sx={{ minWidth: 200 }}
                      onChange={(e) => { setCampaignPage(0); setCampaignFilter((f) => ({ ...f, search: e.target.value })); }} />
                    <Button size="small" onClick={() => {
                      setCampaignPage(0);
                      setCampaignStatus("active");
                      setCampaignFilter({ priority: "", type: "", search: "" });
                    }}>
                      Reset
                    </Button>
                  </Stack>
                  {listsLoading && <LinearProgress sx={{ mb: 1 }} />}
                  <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 880 }}>
                    <TableHead sx={TABLE_HEAD_SX}>
                      <TableRow>
                        <TableCell>Competitor</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Offer</TableCell>
                        <TableCell>Price</TableCell>
                        <TableCell>Runs</TableCell>
                        <TableCell>Priority</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {campaigns.length === 0 && (
                        <TableRow><TableCell colSpan={7}>No campaigns.</TableCell></TableRow>
                      )}
                      {campaigns.map((row) => {
                        const countdown = endsLabel(row.ends_at);
                        return (
                          <TableRow key={row.id} hover>
                            <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>
                              <Typography variant="body2" fontWeight={700}>{row.competitor?.name || row.competitor_id}</Typography>
                              {row.competitor?.domain && (
                                <Typography variant="caption" display="block" color="text.secondary">{row.competitor.domain}</Typography>
                              )}
                            </TableCell>
                            <TableCell sx={{ verticalAlign: "top", whiteSpace: "nowrap" }}>{plainLabel(row.campaign_type)}</TableCell>
                            <TableCell sx={{ verticalAlign: "top", minWidth: 240 }}>
                              <Typography variant="body2" fontWeight={700}>{row.title}</Typography>
                              <Typography variant="body2" sx={{ lineHeight: 1.45 }}>{row.offer_details || row.description}</Typography>
                              {row.source_url && <Link href={row.source_url} target="_blank" rel="noreferrer" sx={{ display: "inline-block", mt: 0.5 }}>Source</Link>}
                            </TableCell>
                            <TableCell>
                              {row.price_before || row.price_after
                                ? `${row.price_before || "—"} → ${row.price_after || "—"}`
                                : "—"}
                            </TableCell>
                            <TableCell>
                              {row.starts_at || "—"} → {row.ends_at || "open"}
                              {countdown && <Box sx={{ mt: 0.5 }}><Chip size="small" label={countdown} /></Box>}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap" }}>{priorityChip(row.priority)}</TableCell>
                            <TableCell sx={{ whiteSpace: "nowrap", width: 160 }}>
                              <Stack spacing={0.75} alignItems="flex-start">
                                <Button
                                  size="small"
                                  variant="contained"
                                  disabled={!!row.acknowledged_at || !!busy[`campaign-${row.id}`]}
                                  onClick={() => setActionTarget({ kind: "campaigns", id: row.id, competitor: row.competitor?.name || row.competitor_name, summary: row.offer_details || row.title })}
                                >
                                  Take action
                                </Button>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  disabled={!!row.acknowledged_at || !!busy[`campaign-${row.id}`]}
                                  onClick={() => acknowledgeCampaign(row.id)}
                                >
                                  {row.acknowledged_at ? "Acknowledged" : "Acknowledge"}
                                </Button>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                  </TableContainer>
                  <ListPager
                    count={data?.campaigns_meta?.total ?? campaigns.length}
                    page={campaignPage}
                    rows={campaignRows}
                    onPage={setCampaignPage}
                    onRows={(rows) => { setCampaignRows(rows); setCampaignPage(0); }}
                  />
                </>
              )}

              {tab === 2 && (
                <Stack spacing={2}>
                  <Box>
                    <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                      <Button variant="outlined" disabled={!!busy.schools || !!scanStatus?.running} onClick={async () => {
                        markBusy("schools", true);
                        try {
                          const res = await api.post("/admin/competitive-intel/scan", { schools_only: true });
                          toast.success(res.data.message || "School scan started.");
                          setScanStatus((prev) => ({ ...(prev || {}), running: true, phase: "schools", current_target: "schools" }));
                        } catch (err) {
                          toast.error(errorMessage(err, "Could not start the school scan."));
                        } finally {
                          markBusy("schools", false);
                        }
                      }}>
                        Scan schools now
                      </Button>
                      <Button
                        variant="contained"
                        disabled={!!busy.verify || !!verifyStatus?.running}
                        onClick={() => openConfirm("filters")}
                      >
                        Check these against school sites
                      </Button>
                    </Stack>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                      Reads the school or consortium page and writes a value only when that page states it. Up to 25 rows matching the filters below. High-priority dates from school sites also run every day at 06:00 London time. No queue.
                    </Typography>
                  </Box>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} flexWrap="wrap" useFlexGap sx={{ p: 1.5, bgcolor: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: 1 }}>
                    <TextField select size="small" label="Priority" value={diffFilter.priority} sx={{ minWidth: 170 }}
                      onChange={(e) => { setDiffPage(0); setDiffFilter((f) => ({ ...f, priority: e.target.value })); }}>
                      <MenuItem value="high,medium">High and medium</MenuItem>
                      <MenuItem value="high">High only</MenuItem>
                      <MenuItem value="medium">Medium only</MenuItem>
                      <MenuItem value="low">Low only</MenuItem>
                      <MenuItem value="high,medium,low">All</MenuItem>
                    </TextField>
                    <TextField select size="small" label="Field" value={diffFilter.group} sx={{ minWidth: 190 }}
                      onChange={(e) => { setDiffPage(0); setDiffFilter((f) => ({ ...f, group: e.target.value })); }}>
                      <MenuItem value="">All fields</MenuItem>
                      <MenuItem value="dates">Dates and registration</MenuItem>
                      <MenuItem value="format">Exam format</MenuItem>
                      <MenuItem value="contact">Contact details</MenuItem>
                      <MenuItem value="details">Gender, region, consortium</MenuItem>
                    </TextField>
                    <TextField select size="small" label="Change" value={diffFilter.kind} sx={{ minWidth: 170 }}
                      onChange={(e) => { setDiffPage(0); setDiffFilter((f) => ({ ...f, kind: e.target.value })); }}>
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="fill">Fills an empty field</MenuItem>
                      <MenuItem value="change">Changes a stored value</MenuItem>
                    </TextField>
                    <TextField select size="small" label="Source" value={diffFilter.source} sx={{ minWidth: 220 }}
                      onChange={(e) => { setDiffPage(0); setDiffFilter((f) => ({ ...f, source: e.target.value })); }}>
                      <MenuItem value="school">School or consortium site</MenuItem>
                      <MenuItem value="directory">Guide or directory</MenuItem>
                      <MenuItem value="">All sources</MenuItem>
                    </TextField>
                    <TextField select size="small" label="Check" value={diffFilter.verification} sx={{ minWidth: 170 }}
                      onChange={(e) => { setDiffPage(0); setDiffFilter((f) => ({ ...f, verification: e.target.value })); }}>
                      <MenuItem value="">All</MenuItem>
                      <MenuItem value="unchecked">Not checked yet</MenuItem>
                      <MenuItem value="verified">Certain</MenuItem>
                      <MenuItem value="needs_review">Needs a person</MenuItem>
                      <MenuItem value="disagreed">Page disagrees</MenuItem>
                      <MenuItem value="unverifiable">Page not read</MenuItem>
                    </TextField>
                    {(data?.diffs_regions || []).length > 0 && (
                      <TextField select size="small" label="Region" value={diffFilter.region} sx={{ minWidth: 180 }}
                        onChange={(e) => { setDiffPage(0); setDiffFilter((f) => ({ ...f, region: e.target.value })); }}>
                        <MenuItem value="">All regions</MenuItem>
                        {data.diffs_regions.map((region) => (
                          <MenuItem key={region.id} value={String(region.id)}>{region.name}</MenuItem>
                        ))}
                      </TextField>
                    )}
                    <TextField size="small" label="School" value={diffFilter.search} sx={{ minWidth: 220 }}
                      onChange={(e) => { setDiffPage(0); setDiffFilter((f) => ({ ...f, search: e.target.value })); }} />
                    <Button size="small" onClick={() => {
                      setDiffPage(0);
                      setDiffFilter({
                        priority: "high,medium", group: "", search: "", kind: "", verification: "", source: "school", region: "",
                      });
                    }}>
                      Reset
                    </Button>
                  </Stack>
                  {listsLoading && <LinearProgress />}
                  {(data?.diffs || []).length === 0 && !listsLoading && (
                    <Typography variant="body2" color="text.secondary">No pending school differences for these filters.</Typography>
                  )}
                  {(data?.diffs || []).map((row) => (
                    <Box key={row.id} sx={{ border: "1px solid #e5e7eb", borderRadius: 1, p: 2 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                        <Typography fontWeight={700}>{row.school_name} — {row.field_changed}</Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          <Chip size="small" variant="outlined" label={row.internal_data ? "Change" : "Fill"} />
                          {row.source_kind === "directory" && <Chip size="small" color="warning" label="Guide, not the school" />}
                          {row.verification_status && (
                            <Chip size="small" variant="outlined" label={CHECK_LABEL[row.verification_status] || row.verification_status} />
                          )}
                          {priorityChip(row.priority)}
                        </Stack>
                      </Stack>
                      <Grid container spacing={1}>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ bgcolor: "#fdecea", p: 1.5, borderRadius: 1, minHeight: 72 }}>
                            <Typography variant="caption" color="text.secondary">Current internal data</Typography>
                            <Typography sx={{ textDecoration: "line-through" }}>{row.internal_data || "Empty"}</Typography>
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Box sx={{ bgcolor: "#e8f5e9", p: 1.5, borderRadius: 1, minHeight: 72 }}>
                            <Typography variant="caption" color="text.secondary">New official data</Typography>
                            <Typography>{row.official_data || "Empty"}</Typography>
                          </Box>
                        </Grid>
                      </Grid>
                      {row.source_url && (
                        <Link href={row.source_url} target="_blank" rel="noreferrer" sx={{ display: "inline-block", mt: 1 }}>
                          Source
                        </Link>
                      )}
                      {row.verified_source_url && row.verified_source_url !== row.source_url && (
                        <Link href={row.verified_source_url} target="_blank" rel="noreferrer" sx={{ display: "inline-block", mt: 1, ml: 2 }}>
                          School page
                        </Link>
                      )}
                      {row.verification_note && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{row.verification_note}</Typography>
                      )}
                      {row.verification_evidence && (
                        <Typography variant="body2" sx={{ mt: 0.5 }}>“{row.verification_evidence}”</Typography>
                      )}
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
                        <Button variant="contained" disabled={!!busy[`diff-${row.id}`]} onClick={() => resolveDiff(row.id, "approved")}>
                          Approve & Update DB
                        </Button>
                        <Button variant="outlined" disabled={!!busy[`diff-${row.id}`]} onClick={() => resolveDiff(row.id, "rejected")}>
                          Reject
                        </Button>
                      </Stack>
                    </Box>
                  ))}
                  <ListPager
                    count={data?.diffs_meta?.total ?? 0}
                    page={diffPage}
                    rows={diffRows}
                    onPage={setDiffPage}
                    onRows={(rows) => { setDiffRows(rows); setDiffPage(0); }}
                  />
                </Stack>
              )}

              {tab === 3 && (
                <>
                  <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ sm: "center" }} sx={{ mb: 1.5 }}>
                    <Button variant="contained" onClick={() => setAddOpen(true)}>Add competitor</Button>
                    <TextField size="small" label="Search" placeholder="Name or domain" value={competitorSearch} sx={{ minWidth: 220 }}
                      onChange={(e) => { setCompetitorPage(0); setCompetitorSearch(e.target.value); }} />
                  </Stack>
                  <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead sx={TABLE_HEAD_SX}>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Domain</TableCell>
                        <TableCell>Found via</TableCell>
                        <TableCell>Last scanned</TableCell>
                        <TableCell>Tracking</TableCell>
                        <TableCell />
                      </TableRow>
                      <TableRow sx={{ "& td": { py: 0.75, bgcolor: "#fff" } }}>
                        <TableCell>
                          <TextField size="small" fullWidth placeholder="Filter" value={competitorColumns.name}
                            inputProps={{ "aria-label": "Filter by name" }}
                            onChange={(e) => setCompetitorColumn("name", e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField size="small" fullWidth placeholder="Filter" value={competitorColumns.domain}
                            inputProps={{ "aria-label": "Filter by domain" }}
                            onChange={(e) => setCompetitorColumn("domain", e.target.value)} />
                        </TableCell>
                        <TableCell>
                          <TextField select size="small" fullWidth value={competitorColumns.via}
                            SelectProps={{ displayEmpty: true }} inputProps={{ "aria-label": "Filter by source" }}
                            onChange={(e) => setCompetitorColumn("via", e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            {competitorSources.map((via) => <MenuItem key={via} value={via}>{via}</MenuItem>)}
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField select size="small" fullWidth value={competitorColumns.scanned}
                            SelectProps={{ displayEmpty: true }} inputProps={{ "aria-label": "Filter by last scan" }}
                            onChange={(e) => setCompetitorColumn("scanned", e.target.value)}>
                            <MenuItem value="">Any time</MenuItem>
                            <MenuItem value="week">Last 7 days</MenuItem>
                            <MenuItem value="older">Over 7 days ago</MenuItem>
                            <MenuItem value="never">Never</MenuItem>
                          </TextField>
                        </TableCell>
                        <TableCell>
                          <TextField select size="small" fullWidth value={competitorColumns.tracking}
                            SelectProps={{ displayEmpty: true }} inputProps={{ "aria-label": "Filter by tracking" }}
                            onChange={(e) => setCompetitorColumn("tracking", e.target.value)}>
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="on">On</MenuItem>
                            <MenuItem value="off">Off</MenuItem>
                          </TextField>
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {visibleCompetitors.length === 0 && (
                        <TableRow><TableCell colSpan={6}>No competitors match.</TableCell></TableRow>
                      )}
                      {visibleCompetitors.slice(competitorPage * competitorRows, (competitorPage + 1) * competitorRows).map((row) => (
                        <TableRow key={row.id}>
                          <TableCell>{row.name}</TableCell>
                          <TableCell>
                            <Link href={row.homepage_url || `https://${row.domain}`} target="_blank" rel="noreferrer">{row.domain}</Link>
                          </TableCell>
                          <TableCell>{row.discovered_via}</TableCell>
                          <TableCell>{formatWhen(row.last_scanned_at)}</TableCell>
                          <TableCell>
                            <Switch
                              checked={!!row.is_tracked}
                              disabled={!!busy[`track-${row.id}`]}
                              onChange={() => toggleTracked(row)}
                              inputProps={{ "aria-label": `Track ${row.name}` }}
                            />
                          </TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>
                            <Button size="small" disabled={!!busy[`scan-${row.id}`]} onClick={() => scanNow(row.id)}>Scan</Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  </TableContainer>
                  <ListPager
                    count={visibleCompetitors.length}
                    page={competitorPage}
                    rows={competitorRows}
                    onPage={setCompetitorPage}
                    onRows={(rows) => { setCompetitorRows(rows); setCompetitorPage(0); }}
                  />
                </>
              )}
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card id="intel-tasks">
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" sx={{ mb: 1 }}>
                <Typography variant="h6">Actionable tasks</Typography>
                <Typography variant="body2" color="text.secondary">{openTasks} open</Typography>
              </Stack>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1 }}>
                <TextField select size="small" label="Priority" value={actionFilter.priority} sx={{ minWidth: 120 }}
                  onChange={(e) => { setActionPage(0); setActionFilter((f) => ({ ...f, priority: e.target.value })); }}>
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="low">Low</MenuItem>
                </TextField>
                <TextField size="small" label="Search" value={actionFilter.search} sx={{ flex: 1 }}
                  onChange={(e) => { setActionPage(0); setActionFilter((f) => ({ ...f, search: e.target.value })); }} />
              </Stack>
              {(data?.actions || []).length === 0 && (
                <Typography variant="body2" color="text.secondary">No tasks yet. They appear after a scan finds something high priority.</Typography>
              )}
              <Stack sx={{ maxHeight: { lg: "70vh" }, overflowY: "auto" }}>
                {(data?.actions || []).map((action) => (
                  <FormControlLabel
                    key={action.id}
                    sx={{ alignItems: "flex-start", ml: 0, mr: 0, py: 0.75, borderBottom: "1px solid #f1f5f9", width: "100%" }}
                    control={
                      <Checkbox
                        sx={{ py: 0 }}
                        checked={!!action.is_completed}
                        disabled={!!busy[`action-${action.id}`]}
                        onChange={() => completeAction(action)}
                      />
                    }
                    label={
                      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ pt: 0.25 }}>
                        {priorityChip(action.priority)}
                        <Typography
                          variant="body2"
                          sx={{ lineHeight: 1.45, textDecoration: action.is_completed ? "line-through" : "none" }}
                        >
                          {action.task_description}
                        </Typography>
                      </Stack>
                    }
                  />
                ))}
              </Stack>
              <ListPager
                count={data?.actions_meta?.total ?? (data?.actions || []).length}
                page={actionPage}
                rows={actionRows}
                onPage={setActionPage}
                onRows={(rows) => { setActionRows(rows); setActionPage(0); }}
              />
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Dialog open={confirmUpdate !== null} onClose={closeConfirm} fullWidth maxWidth="md">
        <DialogTitle>These details might change</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            {confirmUpdate === "dates"
              ? "High-priority dates from school or consortium sites. A date is written only when that page states it."
              : "Differences matching the filters on this page. A value is written only when the school page states it."}
          </Typography>
          {previewLoading && <LinearProgress sx={{ mt: 2 }} />}
          {!previewLoading && previewRows.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Nothing is waiting to be checked for these filters.
            </Typography>
          )}
          {!previewLoading && previewRows.length > 0 && (
            <Box sx={{ maxHeight: "50vh", overflowY: "auto", pr: 0.5 }}>
              <DiffPreviewList rows={previewRows} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeConfirm}>Cancel</Button>
          <Button
            variant="contained"
            disabled={!!busy.verify || !!verifyStatus?.running || previewLoading || previewRows.length === 0}
            onClick={() => {
              const mode = confirmUpdate;
              setConfirmUpdate(null);
              if (mode === "dates") {
                setTab(2);
                setDiffPage(0);
                setDiffFilter({
                  priority: "high",
                  group: "dates",
                  search: "",
                  kind: "",
                  verification: "",
                  source: "school",
                  region: "",
                });
                verifySchools({
                  diffs_priority: "high",
                  diffs_group: "dates",
                  diffs_source: "school",
                  apply: true,
                  limit: 25,
                });
                return;
              }
              verifySchools();
            }}
          >
            Update
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Add competitor</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            <TextField label="Domain" placeholder="example.co.uk" value={draft.domain} onChange={(e) => setDraft({ ...draft, domain: e.target.value })} />
            <TextField label="Notes" multiline minRows={2} value={draft.notes} onChange={(e) => setDraft({ ...draft, notes: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" disabled={!!busy.add || !draft.name || !draft.domain} onClick={addCompetitor}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Edit the scan's task, then create it or dismiss the item. The lists and Action tasks reload after. */}
      <TakeActionDialog
        target={actionTarget}
        onClose={() => setActionTarget(null)}
        onDone={() => load()}
      />
    </Box>
  );
}
